import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useNotifications } from '../../contexts/NotificationContext'
import { useHaptics } from '../../utils/haptics'
import { usePublicSettings, useMoney } from '../../hooks/usePublicSettings'
import { bookingApi, ymdOf, formatYmd, callOutCreditText, pricedForText } from './bookingApi'

// Icons
function ArrowLeftIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  )
}

function CalendarIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <path d="M16 2v4M8 2v4M3 10h18"/>
    </svg>
  )
}

function ChevronLeftIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M15 18l-6-6 6-6"/>
    </svg>
  )
}

function ChevronRightIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M9 18l6-6-6-6"/>
    </svg>
  )
}

function ClockIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 6v6l4 2"/>
    </svg>
  )
}

function ZapIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M13 2L3 14h7l-1 8 10-12h-7z"/>
    </svg>
  )
}

export default function BookingDateTime() {
  const { professionalId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { showError } = useNotifications()
  const haptics = useHaptics()
  const settings = usePublicSettings()
  const money = useMoney()
  const asapMinutes = settings?.['booking.asap_lead_minutes']

  // Carried through from a service card (repair method) or the AI chat pro card (trade + severity), if any
  const carried = location.state || {}

  const [selectedDate, setSelectedDate] = useState(null)   // YYYY-MM-DD (market calendar)
  const [selectedTime, setSelectedTime] = useState(null)   // slot from the server
  const [urgency, setUrgency] = useState('scheduled') // 'asap', 'today', 'scheduled'
  const [days, setDays] = useState([])                  // [{ date, slots }] free slots per day
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [calendarDays, setCalendarDays] = useState([])
  const [quote, setQuote] = useState(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    bookingApi.availability(professionalId)
      .then(a => { if (alive) { setDays(a.days || []); setLoadError(null) } })
      .catch(err => { if (alive) { setDays([]); setLoadError(err.message) } })
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [professionalId])

  const slotsByDate = new Map(days.map(d => [d.date, d.slots]))
  const todayKey = days[0]?.date || null
  const availableTimes = selectedDate ? (slotsByDate.get(selectedDate) || []) : []

  useEffect(() => {
    generateCalendarDays()
  }, [currentMonth, days])

  // Server price for the chosen visit (call-out fee with out-of-hours / Sunday / emergency rules)
  const visitAt = urgency === 'asap' ? 'asap' : selectedTime?.iso
  useEffect(() => {
    if (!visitAt) { setQuote(null); return }
    let alive = true
    bookingApi.quote({ professionalId, at: visitAt, trade: carried.trade, severity: carried.severity, method: carried.method, quantity: carried.quantity })
      .then(q => alive && setQuote(q))
      .catch(() => alive && setQuote(null))
    return () => { alive = false }
  }, [professionalId, visitAt])

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysFromPrevMonth = firstDay.getDay()
    const prevMonthLastDay = new Date(year, month, 0).getDate()
    const totalDays = lastDay.getDate()
    const totalCells = Math.ceil((daysFromPrevMonth + totalDays) / 7) * 7
    const daysFromNextMonth = totalCells - (daysFromPrevMonth + totalDays)

    const cells = []
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i)
      cells.push({ date, key: ymdOf(date), day: prevMonthLastDay - i, isCurrentMonth: false, isToday: false, isPast: true, available: false })
    }
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, month, day)
      const key = ymdOf(date)
      cells.push({
        date, key, day,
        isCurrentMonth: true,
        isToday: key === todayKey,
        isPast: todayKey ? key < todayKey : false,
        available: (slotsByDate.get(key) || []).length > 0   // real free slots for this professional
      })
    }
    for (let day = 1; day <= daysFromNextMonth; day++) {
      const date = new Date(year, month + 1, day)
      cells.push({ date, key: ymdOf(date), day, isCurrentMonth: false, isToday: false, isPast: false, available: false })
    }
    setCalendarDays(cells)
  }

  const handlePrevMonth = () => {
    haptics.light()
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))
  }

  const handleNextMonth = () => {
    haptics.light()
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))
  }

  const handleDateSelect = (key) => {
    haptics.light()
    setSelectedDate(key)
    setSelectedTime(null)
  }

  const handleTimeSelect = (slot) => {
    haptics.light()
    setSelectedTime(slot)
  }

  const handleUrgencyChange = (newUrgency) => {
    haptics.light()
    setUrgency(newUrgency)
    setSelectedTime(null)
    setSelectedDate(newUrgency === 'today' ? todayKey : null)
  }

  const handleContinue = () => {
    const carry = { method: carried.method, quantity: carried.quantity, trade: carried.trade, severity: carried.severity }
    if (urgency === 'asap') {
      haptics.success()
      navigate(`/booking/review/${professionalId}`, {
        state: { ...carry, urgency: 'asap', at: 'asap', date: todayKey, time: 'ASAP' }
      })
      return
    }
    if (!selectedDate || !selectedTime) {
      haptics.error()
      showError(urgency === 'today' ? 'Please select a time slot' : 'Please select both date and time')
      return
    }
    haptics.success()
    navigate(`/booking/review/${professionalId}`, {
      state: {
        ...carry,
        urgency,
        at: selectedTime.iso,
        date: selectedDate,
        time: selectedTime.time,
        timeDisplay: selectedTime.label,
        rate: selectedTime.rate
      }
    })
  }

  const canContinue =
    urgency === 'asap' ||
    (urgency === 'today' && selectedTime) ||
    (urgency === 'scheduled' && selectedDate && selectedTime)

  const fee = quote?.callOut
  const inCurrency = { currency: quote?.currency }
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white px-4 py-4 shadow-lg">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 rounded-xl touch-manipulation"
          >
            <ArrowLeftIcon />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Select Date & Time</h1>
            <p className="text-sm text-slate-500">Choose when you need the service</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6 pb-24">
        {/* Urgency Selection */}
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <h3 className="font-semibold text-slate-900 mb-4">How urgent is this?</h3>
          <div className="space-y-3">
            <button
              onClick={() => handleUrgencyChange('asap')}
              className={`w-full p-4 rounded-xl border-2 text-left transition-all touch-manipulation ${
                urgency === 'asap'
                  ? 'border-slate-900 bg-slate-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <ZapIcon className="text-red-600" />
                <div>
                  <div className="font-medium text-slate-900">ASAP (Emergency)</div>
                  <div className="text-sm text-slate-600">{asapMinutes != null ? `Visit targeted within ${asapMinutes} minutes` : 'As soon as possible'}</div>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleUrgencyChange('today')}
              className={`w-full p-4 rounded-xl border-2 text-left transition-all touch-manipulation ${
                urgency === 'today'
                  ? 'border-slate-900 bg-slate-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <ClockIcon className="text-orange-600" />
                <div>
                  <div className="font-medium text-slate-900">Today</div>
                  <div className="text-sm text-slate-600">Schedule for today if available</div>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleUrgencyChange('scheduled')}
              className={`w-full p-4 rounded-xl border-2 text-left transition-all touch-manipulation ${
                urgency === 'scheduled'
                  ? 'border-slate-900 bg-slate-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <CalendarIcon className="text-blue-600" />
                <div>
                  <div className="font-medium text-slate-900">Scheduled</div>
                  <div className="text-sm text-slate-600">Choose a specific date and time</div>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Date Selection - ONLY show for SCHEDULED */}
        {urgency === 'scheduled' && (
          <div className="bg-white rounded-2xl p-5 shadow-lg">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={handlePrevMonth}
                className="p-2 hover:bg-slate-100 rounded-lg touch-manipulation transition-colors"
              >
                <ChevronLeftIcon />
              </button>
              <h3 className="font-semibold text-slate-900">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h3>
              <button
                onClick={handleNextMonth}
                className="p-2 hover:bg-slate-100 rounded-lg touch-manipulation transition-colors"
              >
                <ChevronRightIcon />
              </button>
            </div>

            {/* Week Day Headers */}
            <div className="grid grid-cols-7 gap-2 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="text-center text-xs font-medium text-slate-400 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {calendarDays.map((dayObj, index) => {
                const isSelected = selectedDate === dayObj.key
                
                return (
                  <button
                    key={index}
                    onClick={() => {
                      if (dayObj.isCurrentMonth && dayObj.available && !dayObj.isPast) {
                        handleDateSelect(dayObj.key)
                      }
                    }}
                    disabled={!dayObj.isCurrentMonth || !dayObj.available || dayObj.isPast}
                    className={`aspect-square rounded-full text-center touch-manipulation transition-all flex items-center justify-center text-sm font-medium ${
                      isSelected
                        ? 'bg-slate-900 text-white shadow-md'
                        : dayObj.isToday
                          ? 'bg-slate-100 text-slate-900 font-bold'
                          : dayObj.isCurrentMonth && dayObj.available && !dayObj.isPast
                            ? 'hover:bg-slate-100 text-slate-900'
                            : 'text-slate-300 cursor-not-allowed'
                    }`}
                  >
                    {dayObj.day}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Time Selection - Show for TODAY and SCHEDULED (when date selected) */}
        {(urgency === 'today' || (urgency === 'scheduled' && selectedDate)) && (
          <div className="bg-white rounded-2xl p-5 shadow-lg">
            <h3 className="font-semibold text-slate-900 mb-4">
              {urgency === 'today' ? 'Available times today' : 'Select time'}
            </h3>
            
            {loading ? (
              <div className="text-sm text-slate-500">Loading free times…</div>
            ) : loadError ? (
              <div className="text-sm text-red-600">Could not load availability: {loadError}</div>
            ) : availableTimes.length === 0 ? (
              <div className="text-sm text-slate-500">No free times on this day. Please pick another date.</div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {availableTimes.map((slot) => (
                  <button
                    key={slot.iso}
                    onClick={() => handleTimeSelect(slot)}
                    title={slot.rate}
                    className={`py-3 px-4 rounded-xl text-sm font-medium touch-manipulation transition-all ${
                      selectedTime && selectedTime.iso === slot.iso
                        ? 'bg-slate-900 text-white shadow-md'
                        : 'hover:bg-slate-100 border border-slate-200 text-slate-900'
                    }`}
                  >
                    {slot.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ASAP Confirmation - Show immediately for emergency */}
        {urgency === 'asap' && (
          <div className="bg-red-50 rounded-2xl p-5 border-2 border-red-200">
            <div className="flex items-start gap-3">
              <ZapIcon className="text-red-600 flex-shrink-0 mt-1" />
              <div className="w-full">
                <h3 className="font-semibold text-slate-900 mb-2">Emergency Service Request</h3>
                <p className="text-sm text-slate-700 mb-3">
                  The professional is notified immediately and confirms the booking on WhatsApp.
                </p>
                <div className="bg-white rounded-lg p-3 text-sm space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Visit targeted:</span>
                    <span className="font-medium text-red-600">{asapMinutes != null ? `Within ${asapMinutes} minutes` : '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Service:</span>
                    <span className="font-medium text-slate-900">As soon as possible</span>
                  </div>
                  {fee && (
                    <div className="border-t border-slate-200 my-2 pt-2">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Emergency call-out fee:</span>
                        <span className="font-medium text-slate-900">{money(fee.fee, inCurrency)}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{fee.rate}</div>
                      <div className={`text-xs mt-1 ${fee.deductible ? 'text-green-600' : 'text-slate-500'}`}>{callOutCreditText(fee.deductible)}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Selected Summary */}
        {canContinue && (
          <div className="bg-slate-50 rounded-2xl p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Booking Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Service Type:</span>
                <span className="font-medium text-slate-900">
                  {urgency === 'asap' ? 'Emergency Service' : 
                   urgency === 'today' ? 'Same Day Service' : 'Scheduled Service'}
                </span>
              </div>
              {urgency !== 'asap' && selectedDate && (
                <div className="flex justify-between">
                  <span className="text-slate-600">Date:</span>
                  <span className="font-medium text-slate-900">{formatYmd(selectedDate)}</span>
                </div>
              )}
              {urgency !== 'asap' && selectedTime && (
                <div className="flex justify-between">
                  <span className="text-slate-600">Time:</span>
                  <span className="font-medium text-slate-900">{selectedTime.label}</span>
                </div>
              )}
              {urgency === 'asap' && (
                <div className="flex justify-between">
                  <span className="text-slate-600">Visit targeted:</span>
                  <span className="font-medium text-red-600">{asapMinutes != null ? `Within ${asapMinutes} minutes` : '—'}</span>
                </div>
              )}

              {fee && (
                <div className="border-t border-slate-300 my-2 pt-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">
                      {urgency === 'asap' ? 'Emergency call-out fee:' : 'Call-out fee:'}
                    </span>
                    <span className="font-medium text-slate-900">{money(fee.fee, inCurrency)}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{fee.rate}</div>
                  {pricedForText(quote) && <div className="text-xs text-slate-500 mt-1">Priced for: {pricedForText(quote)}</div>}
                  <div className={`text-xs mt-1 ${fee.deductible ? 'text-green-600' : 'text-slate-500'}`}>
                    {callOutCreditText(fee.deductible)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 safe-area-inset">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <button
            onClick={handleContinue}
            disabled={!canContinue}
            className={`w-full py-3 rounded-xl font-medium touch-manipulation transition-all ${
              canContinue
                ? 'bg-slate-900 text-white hover:bg-slate-800 shadow-md'
                : 'bg-slate-200 text-slate-500 cursor-not-allowed'
            }`}
          >
            Continue to Review
          </button>
        </div>
      </div>
    </div>
  )
}
